import React, { useEffect, useRef, useState } from "react";
import { Checkbox, Popper } from "@mui/material";
import { Anchor } from "./Styled";
import { CustomMenuItem, ItemTypeTag } from "./Styled";
import SearchBar from "..";
import { Product } from "../../../../data/entities";

interface SearchBarWithSingleSelectSuggestionProps {
    fetchItems: Function;
    menuList: any[];
    handleOnclickItem: Function;
    disableAutoScroll?: boolean;
}

export const SearchBarWithSingleSelectSuggestion = ({
    menuList,
    fetchItems,
    handleOnclickItem,
    disableAutoScroll = false,
}: SearchBarWithSingleSelectSuggestionProps) => {
    const [isAnchored, setIsAnchored] = useState(false);
    const containerRef = useRef(null);
    const anchorRef = useRef(null);

    const handleClose = () => {
        setIsAnchored(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (
                containerRef.current &&
                !(containerRef.current as any).contains(event.target) &&
                anchorRef.current &&
                !(anchorRef.current as any).contains(event.target)
            ) {
                setIsAnchored(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!isAnchored || disableAutoScroll) return;
        const anchor = anchorRef.current as any;
        if (!anchor) return;
        let scroller: any = anchor.parentElement;
        while (scroller && scroller !== document.body) {
            const style = window.getComputedStyle(scroller);
            if (/auto|scroll/.test(style.overflowY) || /auto|scroll/.test(style.overflow)) break;
            scroller = scroller.parentElement;
        }
        const rect = anchor.getBoundingClientRect();
        const margin = 8;
        if (!scroller || scroller === document.body) {
            const delta = rect.bottom - (window.innerHeight - margin);
            if (delta > 0) window.scrollBy({ top: delta, behavior: "smooth" });
        } else {
            const sRect = (scroller as HTMLElement).getBoundingClientRect();
            const deltaDown = rect.bottom - (sRect.bottom - margin);
            if (deltaDown > 0) (scroller as HTMLElement).scrollBy({ top: deltaDown, behavior: "smooth" });
            const deltaUp = (sRect.top + margin) - rect.top;
            if (deltaUp > 0) (scroller as HTMLElement).scrollBy({ top: -deltaUp, behavior: "smooth" });
        }
    }, [disableAutoScroll, isAnchored]);

    return (
        <div
            style={{ position: "relative", width: "600px", maxWidth: "100%" }}
            ref={containerRef}
        >
            <SearchBar
                placeholder="Tìm kiếm theo từ khoá Mã vật tư, Tên vật tư"
                fetchItems={fetchItems}
                onFocus={() => setIsAnchored(true)}
            />
            {isAnchored && (
                <Anchor ref={anchorRef}>
                    {menuList.map((item: any) => (
                        <CustomMenuItem
                            key={item.id}
                            onClick={() => {
                                handleOnclickItem(item);
                                handleClose();
                            }}
                        >
                            {item.name}
                            {item.item_type && (
                                <ItemTypeTag itemType={item.item_type}>
                                    {item.item_type === 'product' ? 'Sản phẩm' : 'Vật tư'}
                                </ItemTypeTag>
                            )}
                        </CustomMenuItem>
                    ))}
                </Anchor>
            )}
        </div>
    );
};

interface SearchBarWithSuggestionProps {
    fetchItems: Function;
    menuList: any[];
    selectedItems: any[];
    handleOnclickItem: Function;
    placeholder?: string;
    style?: React.CSSProperties;
    disableAutoScroll?: boolean;
    renderInPortal?: boolean;
}

const SearchBarWithSuggestion = ({
    menuList,
    fetchItems,
    selectedItems,
    handleOnclickItem,
    placeholder,
    style,
    disableAutoScroll = false,
    renderInPortal = false,
}: SearchBarWithSuggestionProps) => {
    const [isAnchored, setIsAnchored] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const anchorRef = useRef<HTMLDivElement | null>(null);

    const onChangeCheckbox = (
        event: { target: { checked: boolean } },
        item: any
    ) => {
        handleOnclickItem(item, event.target.checked);
    };

    const isSelected = (item: Product) => {
        return selectedItems.some((selectedItem) =>
            selectedItem?.product
            ? selectedItem.product.id === item.id
            : selectedItem?.material
            ? selectedItem.material.id === item.id
            : selectedItem?.customer
            ? selectedItem.customer.id === item.id
            : selectedItem.id === item.id
        );
    };

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (
                containerRef.current &&
                !(containerRef.current as any).contains(event.target) &&
                anchorRef.current &&
                !(anchorRef.current as any).contains(event.target)
            ) {
                setIsAnchored(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!isAnchored || disableAutoScroll || renderInPortal) return;
        const anchor = anchorRef.current as any;
        if (!anchor) return;
        let scroller: any = anchor.parentElement;
        while (scroller && scroller !== document.body) {
            const style = window.getComputedStyle(scroller);
            if (/auto|scroll/.test(style.overflowY) || /auto|scroll/.test(style.overflow)) break;
            scroller = scroller.parentElement;
        }
        const rect = anchor.getBoundingClientRect();
        const margin = 8;
        if (!scroller || scroller === document.body) {
            const delta = rect.bottom - (window.innerHeight - margin);
            if (delta > 0) window.scrollBy({ top: delta, behavior: "smooth" });
        } else {
            const sRect = (scroller as HTMLElement).getBoundingClientRect();
            const deltaDown = rect.bottom - (sRect.bottom - margin);
            if (deltaDown > 0) (scroller as HTMLElement).scrollBy({ top: deltaDown, behavior: "smooth" });
            const deltaUp = (sRect.top + margin) - rect.top;
            if (deltaUp > 0) (scroller as HTMLElement).scrollBy({ top: -deltaUp, behavior: "smooth" });
        }
    }, [disableAutoScroll, isAnchored, renderInPortal]);

    const suggestionMenu = (
        <Anchor
            ref={anchorRef}
            style={renderInPortal ? { position: "static", width: "100%" } : undefined}
        >
            {menuList.map((item: any) => (
                <CustomMenuItem
                    key={item.id}
                    onClick={() => {
                        onChangeCheckbox(
                            { target: { checked: !isSelected(item) } },
                            item
                        );
                    }}
                >
                    <Checkbox checked={isSelected(item)} style={{ marginRight: "2px", alignSelf: "flex-start" }} />
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
                        <span style={{ fontWeight: 500, fontSize: "14px" }}>{item.name}</span>
                        <span
                            style={{
                                fontSize: "12px",
                                color: "inherit",
                                opacity: 0.7,
                                fontFamily: "monospace",
                            }}
                        >
                            {item.product_code || item.material_code || item.provider_code || ""}
                        </span>
                    </div>
                    {item.item_type && (
                        <ItemTypeTag itemType={item.item_type}>
                            {item.item_type === "product" ? "Sản phẩm" : "Vật tư"}
                        </ItemTypeTag>
                    )}
                </CustomMenuItem>
            ))}
        </Anchor>
    );

    const menuWidth = containerRef.current?.clientWidth || 600;

    return (
        <div
            style={{ position: "relative", width: "600px", maxWidth: "100%" }}
            ref={containerRef}
        >
            <SearchBar
                placeholder={placeholder ? placeholder : "Tìm kiếm theo từ khoá Mã sản phẩm, Tên sản phẩm"}
                fetchItems={fetchItems}
                onFocus={() => setIsAnchored(true)}
                style={style}
            />
            {isAnchored && (renderInPortal ? (
                <Popper
                    open
                    anchorEl={containerRef.current}
                    placement="bottom-start"
                    disablePortal={false}
                    modifiers={[
                        {
                            name: "flip",
                            enabled: true,
                            options: { fallbackPlacements: ["top-start"] },
                        },
                        {
                            name: "preventOverflow",
                            enabled: true,
                            options: { padding: 8 },
                        },
                    ]}
                    style={{ zIndex: 1600, width: menuWidth }}
                >
                    {suggestionMenu}
                </Popper>
            ) : suggestionMenu)}
        </div>
    );
};

export default SearchBarWithSuggestion;
